import { keepPreviousData, useQuery } from "@tanstack/react-query";
import type { MonthKey } from "@/lib/month/month";
import { monthKeys } from "@/lib/query";
import { getJson } from "@/lib/time/api-client";
import type { DemandMonthRow, KpiCard, MonthSummaryView, MonthTaskRow } from "@/schemas/month";

/** Month totals for the `span` months ending at `month` (GET /api/months/summary). */
export function useMonthSummary(month: MonthKey, span: number) {
  return useQuery({
    queryKey: monthKeys.summary(month, span),
    queryFn: () =>
      getJson<MonthSummaryView>(
        `/api/months/summary?month=${encodeURIComponent(month)}&span=${span}`,
      ),
    staleTime: 60_000,
    // Switching months keeps the last answer on screen until the next one arrives.
    placeholderData: keepPreviousData,
  });
}

/** Guidance vs logged per assignment for one month (GET /api/months/demands). */
export function useMonthDemands(month: MonthKey) {
  return useQuery({
    queryKey: monthKeys.demands(month),
    queryFn: () =>
      getJson<DemandMonthRow[]>(`/api/months/demands?month=${encodeURIComponent(month)}`),
    staleTime: 60_000,
    // Switching months keeps the last answer on screen until the next one arrives.
    placeholderData: keepPreviousData,
  });
}

/** Hours by task for one month (GET /api/months/tasks). */
export function useMonthTasks(month: MonthKey) {
  return useQuery({
    queryKey: monthKeys.tasks(month),
    queryFn: () => getJson<MonthTaskRow[]>(`/api/months/tasks?month=${encodeURIComponent(month)}`),
    staleTime: 60_000,
    // Switching months keeps the last answer on screen until the next one arrives.
    placeholderData: keepPreviousData,
  });
}

/** KPI scorecard lines for one year (GET /api/kpis). They change nightly at most. */
export function useKpis(year: number) {
  return useQuery({
    queryKey: monthKeys.kpis(year),
    queryFn: () => getJson<KpiCard[]>(`/api/kpis?year=${year}`),
    staleTime: 10 * 60_000,
    placeholderData: keepPreviousData,
  });
}
